import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from 'react';
import { toast } from 'sonner';

import type { TilesetProgressView } from '@/common/data-upload';
import { useUnloadGuard } from '@/hooks/use-unload-guard';
import { t } from '@/i18n';
import { extractRequestErrorMessage } from '@/lib/utils/axios';
import { apiErrorMessage } from '@/queries/common';
import { getTilesetStatus } from '@/queries/data-uploads/get-tileset-status';
import { type UploadTilesetParams, uploadTileset } from '@/queries/data-uploads/upload-tileset';
import { queryClient } from '@/queries/query.client';

export type TilesetUploadStatus = 'uploading' | 'processing' | 'ready' | 'failed';

export type ActiveUpload = {
	tempId: string;
	fileName: string;
	sizeMB: number;
	status: TilesetUploadStatus;
	progress: number;
	// Converter pipeline stage while processing (e.g. "Final compression")
	stage?: string;
	// Percent + ETA while converting. Absent on the proxied path, which reports
	// only a stage name.
	progressView?: TilesetProgressView | null;
	error?: string;
	dataUploadId?: string;
	params: UploadTilesetParams;
};

export type TilesetUploadContextValue = {
	activeUpload: ActiveUpload | null;
	// True only while a file transfer is in flight. A processing conversion does
	// NOT count: it runs server-side and never blocks starting another upload.
	isUploading: boolean;
	startUpload: (params: UploadTilesetParams) => void;
	cancel: () => void;
	retry: () => void;
	dismiss: () => void;
};

const TilesetUploadContext = createContext<TilesetUploadContextValue | null>(null);

// Poll interval while a tileset job is processing (ms)
const POLL_INTERVAL_MS = 5000;
// After this long, drop to the slow interval instead of stopping — conversions of
// large models can legitimately take longer, and the backend sweeper keeps the DB
// in sync regardless, so the card should never freeze on a silent spinner.
const FAST_POLL_WINDOW_MS = 15 * 60 * 1000;
const SLOW_POLL_INTERVAL_MS = 30 * 1000;

export function TilesetUploadProvider({ children }: { children: ReactNode }) {
	const [activeUpload, setActiveUpload] = useState<ActiveUpload | null>(null);
	const abortRef = useRef<AbortController | null>(null);
	const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const cancelledRef = useRef(false);

	const isUploading = activeUpload?.status === 'uploading';
	// Only the upload phase is lost when the tab closes. Once the file is on the
	// tile server the conversion continues server-side (the sweeper + processing
	// watcher pick it up), so don't warn while merely processing.
	useUnloadGuard(isUploading);

	const stopPolling = useCallback(() => {
		if (pollTimerRef.current) {
			clearTimeout(pollTimerRef.current);
			pollTimerRef.current = null;
		}
	}, []);

	const poll = useCallback((dataUploadId: string, startedAt: number) => {
		const tick = async () => {
			if (cancelledRef.current) return;
			try {
				const updated = await getTilesetStatus(dataUploadId);
				const status = updated?.tileset?.status;
				if (status === 'ready') {
					// Broad invalidation so the main map's tile loader also refetches and renders
					// the newly-ready tileset — its query key is not under ['data-uploads', 'list'].
					await queryClient.invalidateQueries({ queryKey: ['data-uploads'] });
					setActiveUpload((prev) => (prev ? { ...prev, status: 'ready' } : prev));
					toast.success(t('files.tileset.tilesetReadyToast', { name: updated?.name ?? '' }));
					return;
				}
				// Still processing (or errored): refresh just the list so its status badge updates.
				await queryClient.invalidateQueries({ queryKey: ['data-uploads', 'list'] });
				if (status === 'error') {
					const serverError = updated?.tileset?.errorMessage;
					setActiveUpload((prev) =>
						prev
							? { ...prev, status: 'failed', error: serverError || t('files.tileset.processingFailedFallback') }
							: prev,
					);
					toast.error(t('files.tileset.tilesetFailedToast', { name: updated?.name ?? '' }), {
						description: serverError || undefined,
					});
					return;
				}
				// Surface the pipeline stage and, on the direct path, the computed
				// percentage and estimate.
				const stage = updated?.tileset?.stage;
				const progressView = updated?.tileset?.progressView ?? null;
				if (stage || progressView) {
					setActiveUpload((prev) => (prev ? { ...prev, ...(stage ? { stage } : {}), progressView } : prev));
				}
			} catch (error) {
				console.error('Tileset status poll error:', error);
			}
			const interval = Date.now() - startedAt > FAST_POLL_WINDOW_MS ? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
			pollTimerRef.current = setTimeout(tick, interval);
		};
		pollTimerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
	}, []);

	const run = useCallback(
		async (params: UploadTilesetParams, tempId: string) => {
			cancelledRef.current = false;
			stopPolling();
			const controller = new AbortController();
			abortRef.current = controller;

			const fileName = params.name ?? params.file.name;
			const sizeMB = params.file.size / (1024 * 1024);

			setActiveUpload({ tempId, fileName, sizeMB, status: 'uploading', progress: 0, params });

			try {
				const created = await uploadTileset({
					...params,
					signal: controller.signal,
					onUploadProgress: (percent) => {
						setActiveUpload((prev) =>
							prev && prev.tempId === tempId && prev.status === 'uploading' ? { ...prev, progress: percent } : prev,
						);
					},
				});

				if (cancelledRef.current) return;

				setActiveUpload((prev) =>
					prev && prev.tempId === tempId
						? { ...prev, status: 'processing', progress: 100, dataUploadId: created?.id }
						: prev,
				);
				await queryClient.invalidateQueries({ queryKey: ['data-uploads', 'list'] });
				toast.success(t('files.upload.modelUploadedGenerating'));

				if (created?.id) {
					poll(created.id, Date.now());
				}
			} catch (error) {
				if (cancelledRef.current || controller.signal.aborted) return;
				console.error('Tileset upload error:', error);
				// Prefer the backend/tile-server message (e.g. "Tile server rejected the
				// upload: unsupported file format") over a generic one. The raw message only
				// decides whether there is one; the shown text is apiErrorMessage, so a coded
				// error appears in the current language.
				const serverError = extractRequestErrorMessage(error) ? apiErrorMessage(error) : null;
				setActiveUpload((prev) =>
					prev && prev.tempId === tempId
						? { ...prev, status: 'failed', error: serverError || t('files.upload.uploadFailedFallback') }
						: prev,
				);
				toast.error(t('files.upload.uploadFailedFallback'), { description: apiErrorMessage(error) });
			}
		},
		[poll, stopPolling],
	);

	const startUpload = useCallback(
		(params: UploadTilesetParams) => {
			// Only a live file transfer blocks a new upload. A processing conversion
			// continues server-side; if its card is replaced here, the processing
			// watcher still announces its completion.
			if (isUploading) {
				toast.error(t('files.upload.uploadBusyToast'));
				return;
			}
			run(params, crypto.randomUUID());
		},
		[isUploading, run],
	);

	const cancel = useCallback(() => {
		if (activeUpload?.status !== 'uploading') return;
		cancelledRef.current = true;
		abortRef.current?.abort();
		stopPolling();
		setActiveUpload(null);
		toast.message(t('files.upload.uploadCancelled'));
	}, [activeUpload, stopPolling]);

	const retry = useCallback(() => {
		if (!activeUpload) return;
		run(activeUpload.params, crypto.randomUUID());
	}, [activeUpload, run]);

	const dismiss = useCallback(() => {
		stopPolling();
		setActiveUpload(null);
	}, [stopPolling]);

	return (
		<TilesetUploadContext.Provider value={{ activeUpload, isUploading, startUpload, cancel, retry, dismiss }}>
			{children}
		</TilesetUploadContext.Provider>
	);
}

export function useTilesetUpload(): TilesetUploadContextValue {
	const ctx = useContext(TilesetUploadContext);
	if (!ctx) throw new Error('useTilesetUpload must be used within a TilesetUploadProvider');
	return ctx;
}
